// «Hoy toca» (Diego, 2-nov-2026): a quién le escribe cada asesor hoy, con qué mensaje, y qué pasó con ese cliente. Todo por WhatsApp: el
// asesor abre el chat con el mensaje ya escrito, lo revisa y lo manda él; nada sale en automático. Aquí van las reglas, sin pantalla ni
// base, para que el servidor y el simulado hagan exactamente lo mismo.
import { z } from 'zod';
import { numero } from '../dinero.ts';
import { fechaHermosillo, sumarDias } from '../tiempo.ts';
import { Fecha, FechaHora, Id, Pesos } from './basicos.ts';
import { CuerpoVenta } from './lista-compra.ts';
import type { opcionesDeVenta } from './lista-compra.ts';
import type { EstadoCotizacion, ResumenCotizacion } from './cotizacion.ts';
import { PROMO_HASTA, PROMO_MSI_MESES, promoVigente } from './promo.ts';

export type OpcionesDeVenta = ReturnType<typeof opcionesDeVenta>;

export const TOPE_HOY_TOCA = 20;
export const DIAS_SEGUIMIENTO_POR_OMISION = 2;
/** Una visita agendada sin fecha entra a la lista a los 7 días de agendarla: nadie se queda olvidado. */
export const DIAS_VISITA_SIN_FECHA = 7;
/** «Sigue interesado» no puede mandar al cliente más lejos que esto: una fecha de 2999 lo desaparecería para siempre. */
export const MAX_DIAS_PROXIMA_FECHA = 366;
export const DIAS_ENTRE_MENSAJES = 7;
export const DIAS_TRAS_CONTESTAR = 3;
export const MAX_MENSAJES_SIN_RESPUESTA = 2;
/** Máximo de mensajes al mes a un cliente (regla de Diego: 2 en noviembre), en todos sus folios y aunque conteste. */
export const MAX_MENSAJES_AL_MES = 2;
/** Primer lunes de enero: a quien no contestó dos mensajes se le deja de escribir hasta aquí. */
export const PAUSA_HASTA = '2027-01-04';
/** Hasta cuándo el mensaje ofrece el 20 %: la fecha sale de la tabla de la promo (promo.ts), la única tabla de fechas. */
export const OFERTA_HASTA: string = PROMO_HASTA;
export const MAX_TOQUES = 40;
export const LIMITE_LISTA_SEGUIMIENTO = 500;

export const PLANTILLAS_SEGUIMIENTO = ['primero', 'segundo', 'tercero'] as const;
export const PlantillaSeguimiento = z.enum(PLANTILLAS_SEGUIMIENTO);
export type PlantillaSeguimiento = z.infer<typeof PlantillaSeguimiento>;

export const ACCIONES_TOQUE = ['mensaje', 'contesto', 'interesado', 'compro_con_otro', 'murio', 'vendido'] as const;
export const AccionToque = z.enum(ACCIONES_TOQUE);
export type AccionToque = z.infer<typeof AccionToque>;

export const MOTIVOS_PERDIDA = ['Dijo que no', 'Precio', 'Todavía no le conviene', 'Crédito', 'Cambió de planes', 'Número equivocado', 'Otro'] as const;
export const MotivoPerdida = z.enum(MOTIVOS_PERDIDA);
export type MotivoPerdida = z.infer<typeof MotivoPerdida>;
export const MOTIVO_COMPRO_CON_OTRO = 'Compró con otro';

/** `manera`: con qué manera de decirlo salió un mensaje (maneraDelMensaje), guardada al anotarlo. Sin ella, la cuenta se rehacía con los
 *  últimos MAX_TOQUES toques del folio y, pasados 40, le tocaba siempre la misma: el mismo texto en cada mensaje desde unos 11 meses de
 *  seguimiento (verificación del 3-nov sobre 05e337e). Ausente = un mensaje anotado antes de guardarla (se rehace como antes). Sin tope de
 *  MANERAS: si un día se quitan textos, un toque viejo se sigue leyendo. */
export const ToqueSeguimiento = z.strictObject({ en: FechaHora, por: Id, accion: AccionToque, plantilla: PlantillaSeguimiento.nullable().optional(), manera: z.int().nonnegative().max(99).optional() });
export type ToqueSeguimiento = z.infer<typeof ToqueSeguimiento>;

/** Se guarda en la cotización. Ausente = nadie le ha dado seguimiento desde aquí (todas las de antes; se leen así, sin migración). */
export const SeguimientoCrm = z.strictObject({
  /** Mensajes abiertos desde «Hoy toca» desde la última vez que el cliente contestó. */
  sin_respuesta: z.int().nonnegative(),
  toques: z.array(ToqueSeguimiento).max(MAX_TOQUES),
  perdida: z
    .strictObject({ motivo: z.string().trim().min(1).max(60), con_quien: z.string().trim().max(80).nullable(), a_cuanto: Pesos.nullable(), en: FechaHora })
    .nullable(),
});
export type SeguimientoCrm = z.infer<typeof SeguimientoCrm>;

const SEGUIMIENTO_VACIO: SeguimientoCrm = { sin_respuesta: 0, toques: [], perdida: null };

const conRevision = { revision: z.int().nonnegative() };
/** `ya_lo_mande`: el servidor dijo que hoy no le tocaba (una pestaña de ayer, o le escribieron desde otro lado) pero el WhatsApp ya se abrió y el
 *  asesor dice que SÍ lo mandó: se anota igual, para que la cuenta y la próxima fecha digan lo que de verdad pasó (verificación del 2-nov, ronda 2). */
export const CuerpoMensajeSeguimiento = z.strictObject({ plantilla: PlantillaSeguimiento, ya_lo_mande: z.literal(true).optional() });
export type CuerpoMensajeSeguimiento = z.infer<typeof CuerpoMensajeSeguimiento>;

const FechaSiguiente = Fecha;
export const CuerpoResultadoSeguimiento = z.discriminatedUnion('accion', [
  z.strictObject({ ...conRevision, accion: z.literal('contesto'), nota: z.string().trim().max(300).optional() }),
  z.strictObject({ ...conRevision, accion: z.literal('interesado'), fecha: FechaSiguiente, nota: z.string().trim().max(300).optional() }),
  z.strictObject({ ...conRevision, accion: z.literal('compro_con_otro'), con_quien: z.string().trim().max(80).nullable(), a_cuanto: Pesos.nullable() }),
  z.strictObject({ ...conRevision, accion: z.literal('murio'), motivo: MotivoPerdida, nota: z.string().trim().max(300).optional() }),
  z.strictObject({ ...conRevision, accion: z.literal('vendido'), venta: CuerpoVenta.optional() }),
]);
export type CuerpoResultadoSeguimiento = z.infer<typeof CuerpoResultadoSeguimiento>;

export const ConsultaHoyToca = z.strictObject({ vendedor_id: z.union([Id, z.literal('todos')]).optional() });

// ───────────── Fechas (días de calendario, en Hermosillo) ─────────────

export function diasEntreFechas(desde: string, hasta: string): number {
  return Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000);
}

const fechaDe = (iso: string): string => fechaHermosillo(new Date(iso));

/** Mandar una cotización sin fecha no se permite: si no la ponen, 2 días. Solo Enviada y Seguimiento: la de una Visita agendada es la de la visita. */
export function fechaSeguimientoPorOmision(hoy: string): string {
  return sumarDias(hoy, DIAS_SEGUIMIENTO_POR_OMISION);
}
export const ESTADOS_CON_FECHA_OBLIGADA: readonly EstadoCotizacion[] = ['enviada', 'seguimiento'];

/** La fecha que queda al guardar el CRM (servidor y simulado). `pedida` undefined = no la tocó; undefined de vuelta = no cambia. Una mandada
 *  no se queda sin fecha: 2 días si no ponen otra, solo al ENTRAR a Enviada o Seguimiento o al borrársela (una de antes sin fecha no se
 *  toca por editarle una nota). Al agendar una visita sin decir cuándo se quita la de antes: era del seguimiento (a veces los 2 días
 *  automáticos), no de la visita, y «Hoy toca» la tomaría por la fecha de la visita (verificación adversarial del 2-nov). */
export function fechaAlGuardarCrm(antes: { estado: EstadoCotizacion; proximo_seguimiento: string | null }, estado: EstadoCotizacion, pedida: string | null | undefined, hoy: string): string | null | undefined {
  const cambia = estado !== antes.estado;
  if (cambia && estado === 'visita_agendada' && pedida === undefined) return antes.proximo_seguimiento === null ? undefined : null;
  if (!ESTADOS_CON_FECHA_OBLIGADA.includes(estado)) return pedida;
  return pedida === null || (cambia && (pedida ?? antes.proximo_seguimiento) === null) ? fechaSeguimientoPorOmision(hoy) : pedida;
}

/** Una fecha del CRM a más de un año (2999, o 2062 por 2026) sacaría al cliente de «Hoy toca» para siempre: se rechaza con el porqué
 *  (verificación del 2-nov, ronda 2: «Sigue interesado» ya tenía el tope y el CRM no). */
export function fechaCrmInvalida(pedida: string | null | undefined, hoy: string): string | null {
  return typeof pedida === 'string' && pedida > sumarDias(hoy, MAX_DIAS_PROXIMA_FECHA) ? `La fecha de seguimiento ${pedida} es de más de un año: revisa el año.` : null;
}

// ───────────── Quién toca hoy ─────────────

const ABIERTAS: readonly EstadoCotizacion[] = ['borrador', 'enviada', 'seguimiento', 'visita_agendada'];
export const estaAbierta = (e: EstadoCotizacion): boolean => ABIERTAS.includes(e);
/** Lo que dice que el CLIENTE ya decidió (compró o dijo que no). Cancelada no: es un folio anulado, casi siempre porque se rehízo en otro. */
const CIERRAN_AL_CLIENTE: readonly EstadoCotizacion[] = ['ganada', 'perdida'];

/** Lo que se lee aparte de la lista: el teléfono vive en el borrador y el PDF en las versiones. El servidor y el simulado lo arman igual
 *  (telefonosQueHacenFalta, borradoresPorRevisarPdf); sin esto no se sabe quién es el mismo cliente ni qué borrador ya tiene propuesta. */
export interface ContextoHoyToca {
  /** id → teléfono del borrador, de todo folio de telefonosQueHacenFalta (el que falte cuenta como sin teléfono). */
  telefonos: ReadonlyMap<string, string | null>;
  /** Borradores con al menos una versión CON PDF. Un «Guardar versión» sin PDF no es una propuesta: ese borrador no sale (verificación del 2-nov). */
  con_pdf: ReadonlySet<string>;
  /** Vendedores que no usan «Hoy toca» (usaHoyToca: los externos). Sus clientes no salen en ninguna lista ni en el tablero: el mensaje diría
   *  «Soy <externo>, de Solarix» y nadie los atiende desde aquí (verificación del 2-nov, ronda 3: seguían en «Todos» y en el tablero). */
  vendedores_sin_hoy_toca: ReadonlySet<string>;
}

/** Un borrador sin PDF todavía no es un cliente esperando. Con PDF puede ya estar mandado: muchos no lo marcan Enviada. */
function cuentaParaSeguimiento(c: Pick<ResumenCotizacion, 'id' | 'estado'>, conPdf: ReadonlySet<string>): boolean {
  return c.estado === 'borrador' ? conPdf.has(c.id) : estaAbierta(c.estado);
}

/** Los borradores cuyo PDF hay que revisar en sus versiones (los que tienen alguna versión: con o sin PDF). */
export function borradoresPorRevisarPdf(lista: readonly Pick<ResumenCotizacion, 'id' | 'estado' | 'version_actual'>[]): string[] {
  return lista.filter((c) => c.estado === 'borrador' && c.version_actual >= 1).map((c) => c.id);
}

const CAMPOS_CLAVE = /[\u0300-\u036f]/g;
const nombreLlano = (n: string): string => n.normalize('NFD').replace(CAMPOS_CLAVE, '').toLowerCase().replace(/\s+/g, ' ').trim();
/** El nombre con que se agrupa (sin acentos ni mayúsculas), por vendedor; las sucursales de un proyecto, por su grupo. */
export function claveCliente(c: Pick<ResumenCotizacion, 'vendedor_id' | 'cliente_nombre' | 'multisitio'>): string {
  return `${c.vendedor_id}|${c.multisitio?.grupo_id ?? nombreLlano(c.cliente_nombre)}`;
}

/** Los 10 dígitos con que se compara el teléfono (los mismos del enlace de WhatsApp), o null. */
export const telefonoDeCliente = (t: string | null | undefined): string | null => numeroWhatsApp(t)?.slice(2) ?? null;

type ConClave = Pick<ResumenCotizacion, 'id' | 'vendedor_id' | 'cliente_nombre' | 'multisitio'>;

/** De qué folios hace falta el teléfono: de todo el que puede salir en la lista o callar a un cliente (abiertos, Ganada y Perdida). El MISMO
 *  número con otro nombre («Juan Pérez» e «Ing. Juan Pérez López») es el mismo WhatsApp: sin leerlo, le llegaban dos mensajes el mismo día
 *  (verificación del 2-nov, ronda 2). El servidor lo guarda en memoria por revisión: se lee una vez por folio. */
export function telefonosQueHacenFalta(lista: readonly Pick<ResumenCotizacion, 'id' | 'estado' | 'version_actual'>[]): string[] {
  return lista.filter((c) => (c.estado === 'borrador' ? c.version_actual >= 1 : estaAbierta(c.estado) || CIERRAN_AL_CLIENTE.includes(c.estado))).map((c) => c.id);
}

/** La clave de cliente de cada folio (id → clave), siempre dentro del MISMO vendedor (quién se queda con un cliente de dos asesores lo decide
 *  Diego). Son el mismo cliente:
 *  - las sucursales de un proyecto (su grupo);
 *  - dos folios con el mismo teléfono, aunque el nombre esté escrito distinto: es el mismo WhatsApp;
 *  - dos folios con el mismo nombre, salvo que tengan teléfonos DISTINTOS (dos «Juan Pérez» del mismo asesor son dos clientes; la lista
 *    le avisa al asesor con `parecidos` por si en realidad es el mismo con el número corregido).
 *  Un folio sin teléfono junto a dos teléfonos distintos con su nombre va aparte: sale con «corrige el teléfono». */
export function clavesDeClientes(lista: readonly ConClave[], telefonos: ReadonlyMap<string, string | null>): Map<string, string> {
  const padre = new Map<string, string>(lista.map((c) => [c.id, c.id]));
  const raiz = (id: string): string => {
    let r = id;
    while (padre.get(r) !== r) r = padre.get(r)!;
    padre.set(id, r);
    return r;
  };
  const unir = (a: string, b: string) => {
    const [x, y] = [raiz(a), raiz(b)];
    if (x !== y) padre.set(x < y ? y : x, x < y ? x : y);
  };
  const tel = (c: ConClave): string | null => telefonoDeCliente(telefonos.get(c.id));
  const primero = new Map<string, string>();
  const juntar = (llave: string, id: string) => {
    const p = primero.get(llave);
    if (p === undefined) primero.set(llave, id);
    else unir(p, id);
  };
  for (const c of lista) {
    if (c.multisitio?.grupo_id) juntar(`g|${c.vendedor_id}|${c.multisitio.grupo_id}`, c.id);
    const t = tel(c);
    if (t) juntar(`t|${c.vendedor_id}|${t}`, c.id);
  }
  const porNombre = new Map<string, ConClave[]>();
  for (const c of lista) if (!c.multisitio?.grupo_id) porNombre.set(claveCliente(c), [...(porNombre.get(claveCliente(c)) ?? []), c]);
  for (const [k, g] of porNombre) {
    const distintos = new Set(g.map(tel).filter((t) => t !== null)).size > 1;
    for (const c of g) if (!distintos) juntar(`n|${k}`, c.id);
      else if (tel(c) === null) juntar(`n|${k}|sin teléfono`, c.id);
  }
  return new Map(lista.map((c) => [c.id, `${c.vendedor_id}|${raiz(c.id)}`]));
}

/** Folios abiertos que se llaman igual que este pero quedaron como OTRO cliente (otro teléfono): la lista y el «Murió» se los enseñan al asesor
 *  por si es el mismo con el número corregido o el de la esposa. No se juntan solos: dos «Rosa Muestra» pueden ser dos personas. */
export function parecidosDe(lista: readonly (ConClave & Pick<ResumenCotizacion, 'folio' | 'estado'>)[], claves: ReadonlyMap<string, string>): Map<string, string[]> {
  const porNombre = new Map<string, (ConClave & Pick<ResumenCotizacion, 'folio' | 'estado'>)[]>();
  for (const c of lista) if (estaAbierta(c.estado) && !c.multisitio?.grupo_id) porNombre.set(claveCliente(c), [...(porNombre.get(claveCliente(c)) ?? []), c]);
  const fuera = new Map<string, string[]>();
  for (const g of porNombre.values())
    for (const c of g) {
      const otros = g.filter((x) => claves.get(x.id) !== claves.get(c.id)).map((x) => x.folio);
      if (otros.length) fuera.set(c.id, [...new Set(otros)].sort());
    }
  return fuera;
}

/** Desde cuándo toca escribirle, según SU folio. Una visita agendada, el día DESPUÉS de su fecha: el día de la visita no se le pregunta «¿qué
 *  te faltó para decidir?» (verificación adversarial del 2-nov). Sin fecha, a los 7 días de agendarla. */
export function venceEl(c: Pick<ResumenCotizacion, 'estado' | 'proximo_seguimiento' | 'estado_desde' | 'actualizada_en'>): string {
  const desde = fechaDe(c.estado_desde ?? c.actualizada_en);
  if (c.estado === 'visita_agendada') return c.proximo_seguimiento ? sumarDias(c.proximo_seguimiento, 1) : sumarDias(desde, DIAS_VISITA_SIN_FECHA);
  return c.proximo_seguimiento ?? sumarDias(desde, DIAS_SEGUIMIENTO_POR_OMISION);
}

/** Lo que reinicia la cuenta de mensajes sin respuesta: que el cliente conteste, y que se cierre (vendido, «Murió», «Compró con otro»). */
const REINICIAN_LA_CUENTA: readonly AccionToque[] = ['contesto', 'interesado', 'vendido', 'murio', 'compro_con_otro'];

/** Un folio con lo que hace falta para leer su historia. El estado es para saber si se cerró (Ganada o Perdida, también a mano en el CRM). */
export type FolioDeHistoria = Pick<ResumenCotizacion, 'seguimiento'> & Partial<Pick<ResumenCotizacion, 'estado' | 'estado_desde' | 'actualizada_en'>>;

/** Lo que ya pasó con el CLIENTE en todos sus folios, junto y en orden. Rehacer la propuesta en otro folio no reinicia la cuenta: el tope de 2
 *  mensajes al mes y la pausa tras dos sin respuesta son del cliente, no del folio (verificación del 2-nov, ronda 2: le llegaban 4 en noviembre).
 *  Lo que sí la reinicia es que el cliente se CIERRE (Ganada o Perdida, con «Murió» o a mano): si regresa con un folio nuevo, lo de antes ya no
 *  cuenta como mensajes sin respuesta (ronda 3: salía hasta el 4-ene y con el «segundo»). El tope de 2 al mes sí cuenta todos los mensajes. */
export function historiaDe(folios: readonly FolioDeHistoria[]): SeguimientoCrm {
  const vistos = new Set<string>();
  const toques = folios
    .flatMap((f) => f.seguimiento?.toques ?? [])
    .filter((t) => {
      // «Murió» deja el mismo toque en cada folio que cerró: cuenta una vez
      const k = `${t.en}|${t.accion}|${t.por}|${t.plantilla ?? ''}`;
      if (vistos.has(k)) return false;
      vistos.add(k);
      return true;
    })
    .sort((a, b) => (a.en < b.en ? -1 : a.en > b.en ? 1 : 0));
  const cerrado = folios.reduce<string>((m, f) => {
    const en = f.estado !== undefined && CIERRAN_AL_CLIENTE.includes(f.estado) ? (f.estado_desde ?? f.actualizada_en ?? '') : '';
    return en > m ? en : m;
  }, '');
  let sin_respuesta = 0;
  for (const t of toques) sin_respuesta = t.en <= cerrado ? 0 : t.accion === 'mensaje' ? sin_respuesta + 1 : REINICIAN_LA_CUENTA.includes(t.accion) ? 0 : sin_respuesta;
  return { sin_respuesta, toques, perdida: null };
}

const mensajesEnElMes = (h: SeguimientoCrm, fecha: string): number => h.toques.filter((t) => t.accion === 'mensaje' && fechaDe(t.en).startsWith(fecha.slice(0, 7))).length;
const primeroDelMesSiguiente = (fecha: string): string => new Date(Date.UTC(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)), 1)).toISOString().slice(0, 10);

/** El primer día, desde `fecha`, en que al cliente le cabe otro mensaje: con 2 ya en ese mes, el 1 del mes siguiente. Vale para cualquier
 *  camino (contestó, sigue interesado, la fecha del CRM): el tope de 2 al mes no se salta porque el cliente conteste (verificación del 2-nov,
 *  ronda 2: con «Contestó» le llegaron 7 en noviembre). */
export function primerDiaConCupo(h: SeguimientoCrm, fecha: string): string {
  return mensajesEnElMes(h, fecha) >= MAX_MENSAJES_AL_MES ? primeroDelMesSiguiente(fecha) : fecha;
}

/** Desde cuándo toca escribirle al CLIENTE: la fecha de su folio más nuevo, pero nunca antes de lo que su historia permite (7 días tras un
 *  mensaje sin respuesta, la pausa tras dos, y el tope de 2 al mes). */
export function venceDelCliente(c: Pick<ResumenCotizacion, 'estado' | 'proximo_seguimiento' | 'estado_desde' | 'actualizada_en'>, h: SeguimientoCrm, hoy: string): string {
  let v = venceEl(c);
  const ultimo = [...h.toques].reverse().find((t) => t.accion === 'mensaje');
  if (ultimo && h.sin_respuesta >= 1) {
    const tras = fechaTrasMensaje(h, fechaDe(ultimo.en));
    if (tras > v) v = tras;
  }
  const cuando = v < hoy ? hoy : v;
  const cupo = primerDiaConCupo(h, cuando);
  return cupo > cuando ? cupo : v;
}

/** Por qué NO se le anota un mensaje hoy, o null si sí le toca. El servidor lo revisa al abrir WhatsApp: una pestaña abierta desde ayer no
 *  gasta el segundo mensaje antes de los 7 días, y un borrador sin PDF no pasa a Enviada (verificación adversarial del 2-nov). `h`: la historia
 *  del cliente (historiaDe sus folios); `conPdf`: si es borrador, que ya tenga un PDF. */
export function motivoSinMensaje(c: Pick<ResumenCotizacion, 'id' | 'folio' | 'cliente_nombre' | 'estado' | 'proximo_seguimiento' | 'estado_desde' | 'actualizada_en'>, hoy: string, h: SeguimientoCrm, conPdf: ReadonlySet<string>): string | null {
  if (!estaAbierta(c.estado)) return `${c.folio} ya se cerró: ya no le toca mensaje.`;
  if (!cuentaParaSeguimiento(c, conPdf)) return `${c.folio} todavía no tiene PDF: no hay propuesta a la cual darle seguimiento.`;
  const vence = venceDelCliente(c, h, hoy);
  return vence > hoy ? `A ${c.cliente_nombre} (${c.folio}) no le toca mensaje hoy, sino el ${vence}: recarga «Hoy toca».` : null;
}

/** Escribirle a un borrador con PDF quiere decir que ya se mandó; a una visita agendada, que la visita ya pasó (solo sale después de su fecha). */
export function estadoTrasMensaje(e: EstadoCotizacion): EstadoCotizacion | null {
  return e === 'borrador' ? 'enviada' : e === 'visita_agendada' ? 'seguimiento' : null;
}

const tocadoEl = (h: Pick<SeguimientoCrm, 'toques'> | null | undefined, hoy: string): boolean => (h?.toques ?? []).some((t) => fechaDe(t.en) === hoy);

/** Uno por cliente: el tope de 20 y el tablero cuentan clientes, no folios (un «Murió» que cierra 3 folios es un cliente). */
function unoPorCliente<T extends { id: string }>(xs: readonly T[], claves: ReadonlyMap<string, string>): T[] {
  const vistas = new Set<string>();
  return xs.filter((c) => {
    const k = claves.get(c.id) ?? c.id;
    if (vistas.has(k)) return false;
    vistas.add(k);
    return true;
  });
}

/** Cuántos clientes atendió HOY este vendedor: los que él tocó (mensaje o resultado), sean de su lista o ya no. Si Diego reasigna un folio ya
 *  atendido, sigue contando para quien lo atendió (verificación del 2-nov, ronda 2: con 5 reasignados, Juan Carlos llegó a 25). `todas`: la
 *  lista de TODOS los vendedores; `claves`: clavesDeClientes de esa lista. */
export function atendidasHoy(todas: readonly Pick<ResumenCotizacion, 'id' | 'seguimiento'>[], vendedorId: string, hoy: string, claves: ReadonlyMap<string, string>): number {
  return unoPorCliente(
    todas.filter((c) => (c.seguimiento?.toques ?? []).some((t) => t.por === vendedorId && fechaDe(t.en) === hoy)),
    claves,
  ).length;
}

export interface ArmadoHoyToca {
  /** Todas las que tocan, de la más vieja a la más nueva, sin tope. */
  pendientes: ResumenCotizacion[];
  /** Los otros folios del mismo cliente, por id de la cotización que sale en la lista. */
  otras: Map<string, ResumenCotizacion[]>;
  /** Clientes ya tocados hoy (mensaje o resultado, por quien sea), también si ya se cerraron. Uno por cliente, no por folio. El tope del día
   *  de cada vendedor se cuenta con atendidasHoy (lo que tocó ÉL). */
  atendidas: ResumenCotizacion[];
  /** De las atendidas, las que siguen abiertas con su mensaje de hoy: todavía falta decir qué pasó. */
  escritas_hoy: ResumenCotizacion[];
  /** La historia del cliente (historiaDe), por id de cada cotización de `pendientes` y de `escritas_hoy`. */
  historia: Map<string, SeguimientoCrm>;
  /** Folios abiertos con el mismo nombre y otro teléfono (parecidosDe), por id. */
  parecidos: Map<string, string[]>;
  /** El folio más nuevo del cliente está Cancelado y el que sale es uno de antes: si el cliente dijo que no, el asesor lo cierra con «Murió». */
  cancelado_despues: Map<string, string>;
}

/** `lista`: las cotizaciones de UN vendedor, sin papelera. Un cliente sale una vez, con su folio más nuevo. `ctx`: ver ContextoHoyToca (los
 *  folios de un vendedor sin «Hoy toca» no salen). */
export function armarHoyToca(todaLaLista: readonly ResumenCotizacion[], hoy: string, ctx: ContextoHoyToca): ArmadoHoyToca {
  const lista = todaLaLista.filter((c) => !ctx.vendedores_sin_hoy_toca.has(c.vendedor_id));
  const claves = clavesDeClientes(lista, ctx.telefonos);
  const grupos = new Map<string, ResumenCotizacion[]>();
  for (const c of lista) grupos.set(claves.get(c.id)!, [...(grupos.get(claves.get(c.id)!) ?? []), c]);
  const pendientes: { c: ResumenCotizacion; vence: string }[] = [];
  const otras = new Map<string, ResumenCotizacion[]>();
  const historia = new Map<string, SeguimientoCrm>();
  const cancelado_despues = new Map<string, string>();
  const historiaDelGrupo = new Map<string, SeguimientoCrm>();
  for (const [k, g] of grupos) {
    const h = historiaDe(g);
    historiaDelGrupo.set(k, h);
    const abiertas = g.filter((x) => cuentaParaSeguimiento(x, ctx.con_pdf)).sort((a, b) => (a.creada_en < b.creada_en ? 1 : a.creada_en > b.creada_en ? -1 : a.id < b.id ? -1 : 1));
    const c = abiertas[0];
    if (!c) continue;
    // Se vendió o se perdió después del último movimiento de esta: quien ya dijo que no, o ya compró, no vuelve a salir. Una cotización
    // hecha o reabierta DESPUÉS de cerrar sí sale: el cliente regresó. Un folio cancelado no calla a nadie (se anuló, no decidió el cliente).
    if (g.some((x) => CIERRAN_AL_CLIENTE.includes(x.estado) && (x.estado_desde ?? x.actualizada_en) >= (c.estado_desde ?? c.creada_en))) continue;
    const vence = venceDelCliente(c, h, hoy);
    if (vence > hoy || tocadoEl(h, hoy)) continue;
    pendientes.push({ c, vence });
    historia.set(c.id, h);
    if (abiertas.length > 1) otras.set(c.id, abiertas.slice(1));
    const cancelada = g.filter((x) => x.estado === 'cancelada' && x.creada_en > c.creada_en).sort((a, b) => (a.creada_en < b.creada_en ? 1 : -1))[0];
    if (cancelada) cancelado_despues.set(c.id, cancelada.folio);
  }
  pendientes.sort((a, b) => (a.vence !== b.vence ? (a.vence < b.vence ? -1 : 1) : a.c.folio < b.c.folio ? -1 : 1));
  const tocadas = lista.filter((c) => tocadoEl(c.seguimiento, hoy));
  const atendidas = unoPorCliente(tocadas, claves);
  // Si ya dijo qué pasó, el último toque es el resultado: ya no le falta nada y sale de la lista.
  const escritas_hoy = tocadas.filter((c) => {
    const ultimo = c.seguimiento?.toques[c.seguimiento.toques.length - 1];
    return estaAbierta(c.estado) && ultimo?.accion === 'mensaje' && fechaDe(ultimo.en) === hoy;
  });
  for (const c of escritas_hoy) historia.set(c.id, historiaDelGrupo.get(claves.get(c.id)!)!);
  return { pendientes: pendientes.map((x) => x.c), otras, atendidas, escritas_hoy, historia, parecidos: parecidosDe(lista, claves), cancelado_despues };
}

/** Cuántas caben hoy: 20 al día, contando las que ya atendió. */
export function cabenHoy(atendidas: number): number {
  return Math.max(0, TOPE_HOY_TOCA - atendidas);
}

// ───────────── El mensaje ─────────────

export function plantillaDe(seg: SeguimientoCrm | null | undefined): PlantillaSeguimiento {
  if (!seg) return 'primero';
  if (seg.sin_respuesta >= 1) return 'segundo';
  const ultima = seg.toques[seg.toques.length - 1];
  return ultima && (ultima.accion === 'contesto' || ultima.accion === 'interesado') ? 'tercero' : 'primero';
}

/** Los títulos se escriben siempre igual: «ing juan» → «Ing. Juan», «C.P. Luis» se queda «C.P.» (no «C.p.»). */
const TITULOS: Record<string, string> = { sr: 'Sr.', sra: 'Sra.', srta: 'Srta.', don: 'Don', dona: 'Doña', ing: 'Ing.', inge: 'Ing.', lic: 'Lic.', arq: 'Arq.', dr: 'Dr.', dra: 'Dra.', mtro: 'Mtro.', mtra: 'Mtra.', maestro: 'Maestro', maestra: 'Maestra', prof: 'Prof.', profa: 'Profa.', profr: 'Profr.', profe: 'Profe', cp: 'C.P.', contador: 'Contador', contadora: 'Contadora', familia: 'Familia', fam: 'Familia' };
/** Palabras que no son el nombre de UNA persona («Hermanos Pérez», «Sucesión…»): ahí va «Hola.» y no «Hola, Hermanos.». */
const COLECTIVOS = new Set(['hermanos', 'hnos', 'hermanas', 'sucesion', 'grupo', 'herederos', 'casa', 'rancho', 'ejido', 'colonia', 'condominio', 'residencial', 'fraccionamiento', 'privada', 'cliente', 'clienta']);
/** «JUAN» o «juan» → «Juan»: un nombre capturado todo en mayúsculas se lee a robot. «McGregor» se queda como está. */
const conMayuscula = (p: string): string => (p === p.toUpperCase() || p === p.toLowerCase() ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : p);
const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e']);
/** Lo que dice la razón social y no se dice al saludar: «S.A. de C.V.», «SA DE CV», «S. de R.L.», «A.C.»… También si es TODO el nombre. */
const RAZON_SOCIAL = /(^|[\s,]+)(s\.?\s*a\.?\s*p\.?\s*i\.?|s\.?\s*a\.?\s*b\.?|s\.?\s*a\.?|s\.?\s*de\s+r\.?\s*l\.?|s\.?\s*c\.?|a\.?\s*c\.?|s\.?\s*c\.?\s*p\.?|s\.?\s*a\.?\s*s\.?)(\s*de\s*c\.?\s*v\.?)?\.?\s*$/i;
const tituloDe = (p: string): string | undefined => TITULOS[sinAcentosMinus(p).replace(/\./g, '')];
/** Nombres de pila que se abrevian con punto: «Ma. Elena» es María Elena (al saludar se escribe completo). */
const ABREVIATURAS: Record<string, string> = { ma: 'María', fco: 'Francisco', gpe: 'Guadalupe' };
/** Un negocio que se llama solo así («Grupo SA» sin la razón social) no tiene a quién nombrar: «Hola.». */
const GENERICOS_NEGOCIO = new Set([...COLECTIVOS, 'empresa', 'negocio', 'corporativo', 'compania', 'sociedad', 'comercializadora', 'constructora', 'inmobiliaria', 'servicios']);
/** Consonantes con que empieza una palabra en español («pr», «tl» de Tlaquepaque, «ps»). */
const ATAQUES = new Set(['bl', 'br', 'cl', 'cr', 'ch', 'dr', 'fl', 'fr', 'gl', 'gr', 'kl', 'kr', 'll', 'pl', 'pr', 'tr', 'tl', 'tz', 'ps']);
/** Las del inglés, que en Hermosillo salen en nombres de negocio («SPA», «SMART», «STREET», «THE», «SCHOOL», «WRAP»). */
const ATAQUES_INGLES = new Set(['sc', 'sk', 'sl', 'sm', 'sn', 'sp', 'st', 'sw', 'sh', 'th', 'ph', 'wh', 'wr', 'kn', 'gn', 'tw', 'dw', 'scr', 'spr', 'str', 'spl', 'sq', 'shr', 'thr', 'sch', 'schr', 'chr', 'phr', 'xtr']);
/** Siglas que se leen como palabra y en mayúsculas así se escriben («IMSS», «SAT»): las que salen en Hermosillo. Las que no se pueden leer como
 *  palabra («CFE», «SNTE», «HSBC», «OXXO») las reconoce pareceSigla sin lista. */
const SIGLAS = new Set(['imss', 'issste', 'isssteson', 'sat', 'sep', 'dif', 'ine', 'ipn', 'unam', 'itson', 'unison', 'uvm', 'ues', 'ith', 'crit', 'ibm', 'ups', 'gnc', 'heb', 'abc', 'capufe', 'uam', 'uas', 'uaz', 'uat', 'uach', 'unid']);
/** Palabras de verdad hechas de una vocal y consonantes («ART Studio», «Hotel INN»): no son siglas. */
const PALABRAS_VOCAL_Y_CONSONANTES = new Set(['art', 'arts', 'inn', 'all', 'and', 'end', 'ends', 'act', 'acts', 'ask', 'ink', 'elk', 'opt', 'odd', 'add', 'egg', 'ill', 'arm', 'arms', 'ant', 'ants', 'ash', 'itch', 'inch', 'elf', 'elm', 'alps', 'ohm', 'orb']);
/** Dos consonantes con que sí termina una palabra en español o en inglés («SPORT», «BURGERS», «JAZZ», «XÓCHITL»). */
const FINALES = new Set(['nd', 'nt', 'rt', 'st', 'ck', 'ng', 'ss', 'll', 'rs', 'ts', 'ks', 'ps', 'ms', 'ns', 'ls', 'th', 'sh', 'ch', 'rd', 'rk', 'lt', 'ld', 'mp', 'ft', 'pt', 'xt', 'wn', 'rn', 'rm', 'lm', 'lk', 'nk', 'sk', 'sp', 'ff', 'tt', 'rf', 'rl', 'lf', 'lp', 'lb', 'ct', 'gh', 'ph', 'bs', 'ds', 'gs', 'ws', 'cs', 'fs', 'rb', 'rg', 'rp', 'nc', 'mb', 'mn', 'gn', 'wl', 'wk', 'wd', 'zz', 'dd', 'gg', 'nn', 'tz', 'nz', 'rx', 'nx', 'tl']);
/** ¿Una palabra en mayúsculas es una sigla («CFE», «IMSS», «OXXO») y no una palabra a gritos («NORTE», «PIZZA», «SPA», «GYM», «COPPEL»)? Lo es
 *  si no tiene vocal («HSBC», «KFC»), si empieza con consonantes con que no empieza ninguna palabra en español ni en inglés («CFE», «SNTE»,
 *  «BBVA»), si dobla una letra que ninguno de los dos dobla («OXXO») o si es una de SIGLAS. Una palabra de verdad que dobla («PIZZA»,
 *  «COPPEL», «EXPRESS») o que empieza como en inglés («SPA», «STAR») se escribe con mayúscula inicial (verificación del 3-nov: la regla de la
 *  ronda 3 dejaba «Hola, PIZZA Hut.», «SPA Bella», «GYM Power» y «COPPEL»). */
function pareceSigla(p: string): boolean {
  const l = p.normalize('NFD').replace(CAMPOS_CLAVE, '').toLowerCase();
  if (!/^[a-zñ]{2,}$/.test(l) || l === 'mc') return false;
  if (SIGLAS.has(l)) return true;
  const ataque = /^[^aeiouy]*/.exec(l)![0];
  if (ataque === l) return true;
  if (ataque.length >= 2 && !ATAQUES.has(ataque) && !ATAQUES_INGLES.has(ataque) && !/^mc./.test(l)) return true;
  // Las que empiezan con vocal no tienen ataque que revisar (verificación del 3-nov sobre 05e337e: «UTH», «ITESM», «UABC», «ADT», «ETN» y «ABB»
  // salían «Uth», «Itesm»…): una vocal y solo consonantes, corta («UTH», «UDG», «ATT», «UTS»), salvo palabras de verdad («ART», «INN»)…
  if (/^[aeiou][^aeiouy]{2,3}$/.test(l) && !PALABRAS_VOCAL_Y_CONSONANTES.has(l)) return true;
  // …o que termina en dos consonantes con que no termina una palabra en español ni en inglés («UABC», «ITESM», «ABB»; sí «SPORT», «BURGERS»).
  const final = /[^aeiouy]{2}$/.exec(l)?.[0];
  if (final && !FINALES.has(final)) return true;
  return /(xx|jj|qq|vv|ww|hh|kk|yy)|(.)\2\2/.test(l);
}
/** Abreviaturas con punto que parecen nombre («Edo.» es Eduardo; «Cap.», «Gral.» son grados): no se saluda con ellas. */
const NO_SON_NOMBRES = new Set(['edo', 'cap', 'gral']);
/** ¿Una palabra con punto es una abreviatura o una inicial («J.», «Fdo.», «Ant.», «Gmo.») y no un nombre con un punto de más («Juan.»)? Dos
 *  letras o menos, sin vocal, empieza con consonantes con que no empieza un nombre, o termina en dos consonantes como no termina un nombre
 *  («Ant.», «Hdz.»; «Ruth.», «Edith.» y «Xóchitl.» sí son nombres). */
function pareceAbreviatura(p: string): boolean {
  const l = p.normalize('NFD').replace(CAMPOS_CLAVE, '').toLowerCase().replace(/\./g, '');
  if (l.length <= 2 || !/^[a-zñ]+$/.test(l) || NO_SON_NOMBRES.has(l)) return true;
  const ataque = /^[^aeiouy]*/.exec(l)![0];
  // Los nombres también empiezan como en inglés («Christian.», «Stephanie.», «Sharon.», «Thalía.»: salían «Hola.»; verificación del 3-nov
  // sobre 05e337e); de tres letras con dos consonantes al principio, no («Sta.», «Sto.»).
  if (ataque === l || (ataque.length >= 2 && (l.length <= 3 || (!ATAQUES.has(ataque) && !ATAQUES_INGLES.has(ataque))))) return true;
  return !/[aeiouy]/.test(l.at(-1)!) && !/[aeiouy]/.test(l.at(-2)!) && !/(th|tl)$/.test(l);
}
/** Una palabra del nombre como se saluda: sin el punto de más («Juan.» → «Juan»), o null si es una inicial o una abreviatura que no se conoce
 *  («J.», «Fdo.»): no se adivina el nombre. */
function palabraDeNombre(p: string): string | null {
  if (!/[a-záéíóúñü]/i.test(p)) return null;
  const sin = p.replace(/\.+$/, '');
  // Sin vocal no es un nombre sino unas iniciales («JL Pérez» salía «Hola, Jl.»)
  if (!/[aeiouyáéíóúü]/i.test(sin)) return null;
  if (sin !== p && pareceAbreviatura(sin)) return null;
  if (sin.includes('.') || sinAcentosMinus(sin).length < 2) return null;
  return conMayuscula(sin);
}
/** El nombre de pila con que se saluda, desde la primera palabra que debería serlo: «Juan Pérez» → «Juan», «Ma. Elena Ruiz» → «María Elena»,
 *  «Juan.» → «Juan»; '' si no hay a quién nombrar («J. Luis», «Hermanos…», «De La Cruz…»). Igual sin título que tras un título («Sra. Ma.
 *  Elena» → «Sra. María Elena»; verificación del 3-nov: salía «Hola, Sra. Ma.» y «Hola, Sr. J.»). */
function dePila(ps: readonly string[]): string {
  const a = ps[0];
  if (!a) return '';
  const llano = sinAcentosMinus(a).replace(/\./g, '');
  // «Ma. Elena» → «María Elena», «Ma. de Jesús» → «María de Jesús» (ronda 3: salía «Hola, Ma.»).
  const completo = ABREVIATURAS[llano];
  if (completo) {
    const sigue: string[] = [];
    for (const p of ps.slice(1)) {
      if (PARTICULAS.has(sinAcentosMinus(p))) sigue.push(sinAcentosMinus(p));
      else {
        const w = palabraDeNombre(p);
        if (w) sigue.push(w);
        break;
      }
    }
    while (sigue.length && PARTICULAS.has(sigue[sigue.length - 1]!)) sigue.pop();
    return [completo, ...sigue].join(' ');
  }
  if (COLECTIVOS.has(llano) || PARTICULAS.has(llano)) return '';
  return palabraDeNombre(a) ?? '';
}
/** Nombres de pila comunes en Hermosillo, sin acentos ni tilde y en minúsculas (como los deja sinAcentosMinus): solo para decidir qué lado de
 *  una coma es el nombre («Juan, Pitic», «Pérez, Juan»). Fuera los que también son apellido o lugar comunes (Cruz, Reyes, Santos, Ramos, Rosas,
 *  León, Martín, Santiago, Domingo, Paz, Ángeles, Dolores, Trinidad): con ellos no se resuelve la duda y sale «Hola.». */
const NOMBRES_DE_PILA = new Set(
  (
    'juan jose luis carlos jesus miguel francisco jorge antonio alejandro manuel pedro ricardo roberto fernando javier eduardo rafael sergio daniel ' +
    'david raul mario oscar arturo alberto enrique hector victor gerardo ramon alfredo armando guillermo gabriel jaime rodrigo julio cesar ernesto ' +
    'gustavo hugo ignacio adrian alfonso andres angel benjamin diego emilio felipe gilberto gonzalo ivan joaquin jonathan leonardo lorenzo marco ' +
    'mauricio octavio omar pablo rogelio ruben salvador samuel sebastian tomas ulises vicente abel abraham adan agustin alan aldo alonso alvaro ' +
    'arnoldo bernardo braulio bruno cristian christian cristobal efrain eliseo elias emanuel emmanuel erick erik esteban everardo ezequiel fabian ' +
    'federico fidel gael german gregorio heriberto hernan horacio humberto isaac ismael israel jacobo jairo joel josue julian kevin leonel leopoldo ' +
    'lucas marcelo mateo matias moises nicolas noe orlando osvaldo patricio raymundo reynaldo rigoberto rolando rosendo saul teodoro uriel valentin ' +
    'yahir rene edgar edgardo alexis axel brandon bryan damian dario emiliano isidro jacinto nestor norberto ramiro rodolfo simon abelardo ' +
    'adalberto aurelio baltazar benito clemente eleazar faustino genaro hilario jeronimo lazaro leobardo lucio marcelino maximiliano santino steven ' +
    'william thiago iker ian leandro pepe paco pancho lalo chuy tono memo beto nacho chema quique kike poncho rafa chava tavo juanito ' +
    'maria ana guadalupe lupita lupe rosa martha marta patricia elizabeth leticia gabriela alejandra claudia laura adriana veronica silvia monica ' +
    'yolanda sandra norma araceli teresa margarita elena carmen josefina juana francisca blanca alicia irma angelica beatriz lucia cecilia diana ' +
    'karla carla mariana daniela fernanda paola andrea valeria ximena jimena sofia regina camila valentina natalia isabel isabella victoria renata ' +
    'fatima karina carolina liliana gloria susana rocio lorena miriam mirna olga pilar raquel rebeca ruth sara sarah esther ester estela elsa edith ' +
    'erika erica esmeralda eva fabiola graciela griselda hilda ines irene itzel ivonne jazmin jessica judith julia julieta karen leonor lidia lilia ' +
    'luisa luz magdalena maribel maricela marisol marisela mayra nancy nayeli nelly noemi ofelia perla reyna rosario rosalba rosalia selene sonia ' +
    'tania viridiana wendy xochitl yadira yesenia yazmin zulema amparo aurora brenda catalina consuelo emma esperanza eugenia evelyn flor gisela ' +
    'hortensia imelda janet jacqueline jaqueline lourdes marcela maritza mercedes minerva paula paulina pamela priscila ramona rosaura soledad ' +
    'socorro abigail adela adelina aida alma amalia amelia angela antonia azucena berenice bertha celia clara concepcion cristina delia dulce elisa ' +
    'eloisa elvira emilia estefania eunice georgina gladys ileana ingrid isela ivette juliana lizbeth lucero manuela marina marlene matilde mireya ' +
    'nadia nora petra rita rosalinda stephanie sharon shirley thalia vanessa vanesa yuridia citlali yaretzi ariadna arely aylin frida kimberly ' +
    'melissa michelle montserrat monserrat nicole samantha tere paty lety chayo vicky'
  ).split(' '),
);
/** ¿El saludo («Juan», «María Elena», «Ing. Juan») empieza con un nombre de pila conocido (después del título, si lo trae)? */
function empiezaConNombreDePila(saludo: string): boolean {
  const primera = saludo.split(' ').find((p) => !tituloDe(p)) ?? '';
  return NOMBRES_DE_PILA.has(sinAcentosMinus(primera).replace(/\./g, ''));
}
/** «Sra. de la Cruz», «Sr. y Sra. Pérez»: tras un título, las partículas van en minúscula y los títulos se escriben igual. */
const trasTitulo = (ps: readonly string[]): string => ps.map((p) => tituloDe(p) ?? (PARTICULAS.has(sinAcentosMinus(p)) ? sinAcentosMinus(p) : conMayuscula(p))).join(' ');

/** En casa, su nombre de pila («Ing. Juan»; con título y apellido, «Sra. de la Cruz»); en un negocio, su nombre sin la razón social y sin
 *  gritar («GASOLINERA DEL NORTE SA DE CV» → «Gasolinera del Norte»). Si no hay a quién nombrar («Casa Castillo», «De La Cruz Juan»,
 *  «Hermanos Pérez», «Ing.» solo), sin nombre: «Hola.» nunca está mal; «Hola, Casa.», «Hola, De.» o «Hola, Ing..» sí. */
export function saludoDe(cliente: string, esCasa: boolean): string {
  const limpio = cliente.trim().replace(/\s+/g, ' ');
  if (esCasa) {
    // «Mª Elena» es «Ma. Elena» (salía «Hola, Mª.»).
    const conMa = limpio.replace(/(^|\s)Mª\.?(?=\s|$)/giu, '$1Ma.');
    if (conMa !== limpio) return saludoDe(conMa, true);
    // Con coma (salían «Hola, Pérez,.» y «Hola, Juan,.»; verificación del 3-nov sobre 05e337e): si solo un lado da un nombre, ese («Juan,»,
    // «Juan Pérez, Ing.», «Juan Pérez, casa»). Si los dos dan uno distinto: con un título después, lo de después («Pérez, Sra. Ma. Elena»);
    // si no, el lado que empieza con un nombre de pila conocido cuando el otro no («Pérez, Juan» y «Pérez López, Juan Carlos» → Juan; «Juan,
    // Pitic», «Juan Pérez, Pitic», «Carlos, Hermosillo» → el de antes). Por la forma no se distingue «Apellido, Nombre» de «Nombre, una nota»:
    // contar palabras saludaba «Hola, Pitic.», «Hola, San.» y «Hola, Hermosillo.» (re-verificaciones del 3-nov sobre d4c04cc y e15b084). Si
    // los dos lados o ninguno empiezan con uno («Juan Pérez, María López», «Valenzuela, Pitic»), «Hola.», que nunca está mal.
    const coma = limpio.indexOf(',');
    if (coma >= 0) {
      const antes = limpio.slice(0, coma).trim();
      const despues = limpio.slice(coma + 1).replace(/,/g, ' ').trim();
      const a = antes ? saludoDe(antes, true) : '';
      const d = despues ? saludoDe(despues, true) : '';
      if (!a || !d || a === d) return a || d;
      if (tituloDe(despues.split(' ')[0]!)) return d;
      const pa = empiezaConNombreDePila(a);
      const pd = empiezaConNombreDePila(d);
      return pa === pd ? '' : pd ? d : a;
    }
  }
  if (!esCasa) {
    const sinRazon = limpio.replace(RAZON_SOCIAL, '').replace(/[\s,.;:]+$/, '').trim();
    if (GENERICOS_NEGOCIO.has(sinAcentosMinus(sinRazon).replace(/\./g, ''))) return '';
    const gritado = sinRazon === sinRazon.toUpperCase() && /[A-ZÁÉÍÓÚÑ]{3}/.test(sinRazon);
    const nombre = gritado ? sinRazon.split(' ').map((p, i) => (i > 0 && PARTICULAS.has(sinAcentosMinus(p)) ? sinAcentosMinus(p) : pareceSigla(p) ? p : conMayuscula(p))).join(' ') : sinRazon;
    return nombre.length >= 2 ? nombre : '';
  }
  const ps = limpio.split(' ').filter(Boolean);
  const a = ps[0];
  if (!a) return '';
  const titulo = tituloDe(a);
  if (titulo) {
    const resto = ps.slice(1);
    if (!resto.length) return '';
    // «Ing. Juan Pérez López» → «Ing. Juan»; «Sra. de la Cruz» y «Sr. y Sra. Pérez» → completos (son título y apellido). Sin un apellido
    // después («Sra. de», «Sr. y Sra.») no hay a quién nombrar: «Hola.» (ronda 3: salía «Hola, Sra. de.»).
    if (PARTICULAS.has(sinAcentosMinus(resto[0]!)) || tituloDe(resto[0]!)) {
      const tramo = resto.slice(0, 4);
      while (tramo.length && PARTICULAS.has(sinAcentosMinus(tramo[tramo.length - 1]!))) tramo.pop();
      return tramo.some((p) => !PARTICULAS.has(sinAcentosMinus(p)) && !tituloDe(p)) ? `${titulo} ${trasTitulo(tramo)}` : '';
    }
    // Tras el título, el nombre se lee igual que sin título: «Sra. Ma. Elena» → «Sra. María Elena», «Sr. J. Luis» → «Hola.».
    const pila = dePila(resto);
    return pila ? `${titulo} ${pila}` : '';
  }
  return dePila(ps);
}

/** «Juan Carlos Cortés» → «Juan Carlos»; «Miguel Cota» → «Miguel». */
export function asesorDe(nombre: string): string {
  const p = nombre.trim().split(/\s+/).filter(Boolean);
  return p.length >= 3 && !PARTICULAS.has(p[1]!.toLowerCase()) ? `${p[0]} ${p[1]}` : (p[0] ?? nombre);
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const pesos = (n: number): string => `$${numero(n, 0)}`;
const sinAcentosMinus = (s: string): string => s.normalize('NFD').replace(CAMPOS_CLAVE, '').toLowerCase().trim();

/** La promo que ofrece el mensaje, con las cifras del MOTOR (promoDelMensaje, en promo-mensaje.ts): lo que cobra el PDF de la opción
 *  recomendada con la promo y el contado normal del mismo cálculo, y los dos porcentajes de ese cálculo. Aquí no se calcula ningún precio. */
export interface PromoDelMensaje {
  /** Contado con IVA con la promo: el que imprime el PDF. */
  contado: number;
  /** Contado con IVA sin la promo, del mismo cálculo: con el que se compara. */
  contado_normal: number;
  kwp: number | null;
  /** El descuento de contado normal (el de la tabla) y el de la promo, como los usó el motor. Al cliente solo se le dice el de la promo
   *  (Diego, 3-nov-2026): el normal y el contado normal son para comparar con su PDF, nunca van en el texto. */
  pct_normal: number;
  pct_promo: number;
  /** La mensualidad de los 18 meses sin intereses de la promo (lista ÷ 18, con IVA), solo en casa y con la lista hasta el tope; null = el
   *  PDF de la promo no los lleva (negocio, o lista arriba de $500,000). Ausente = tampoco (cifras armadas antes del 3-nov-2026). */
  msi18?: number | null;
  /** Cómo se llama HOY la promo y su fecha límite (promoAlDia): «Buen Fin» al 17-nov del 3 al 17-nov (Diego, 3-nov-2026); si no, «de fin de año» al 31-dic. */
  nombre: string;
  vence: string;
  /** El PDF que el cliente ya tiene trae ESTAS cifras con la promo: no hay nada que «actualizar». */
  en_su_pdf: boolean;
}

export interface DatosMensaje {
  cliente_nombre: string;
  tipo: string;
  ciudad: string;
  asesor: string;
  /** La promo con las cifras del motor, solo si son las de un PDF del cliente y las de uno nuevo de hoy (servidor: promoParaElMensaje). Null =
   *  el mensaje NO ofrece la promo: no aplica a este folio (híbrido, respaldo, precio a mano, precio único, varias sucursales…), su PDF no
   *  saldría, no hay cifra del motor, o la cifra de hoy ya no es la del PDF que tiene el cliente. */
  promo: PromoDelMensaje | null;
  creada_en: string;
  /** Qué manera de decirlo (maneraDelMensaje, de 0 a MANERAS − 1): así un cliente nunca recibe un texto igual a alguno de sus últimos 3, que no
   *  parezca robot. 0 o ausente = la de siempre, la única que ofrece la promo; las otras van sin oferta ni cifras. */
  manera?: number;
}

/** La oferta es para casas en Hermosillo («HMO» también) y mientras haya promo (promo.ts: del 1-nov al 31-dic): a un negocio o a otra
 *  ciudad no se le promete. Además hace falta la cifra del motor (DatosMensaje.promo): sin ella, tampoco se ofrece. */
export function ofertaAplica(d: Pick<DatosMensaje, 'tipo' | 'ciudad'>, hoy: string): boolean {
  return promoVigente(hoy) !== null && hoy <= OFERTA_HASTA && d.tipo === 'casa' && /^(hermosillo|hmo)\b/.test(sinAcentosMinus(d.ciudad));
}

/** «31 de diciembre». */
const diaYMes = (f: string): string => `${Number(f.slice(8, 10))} de ${MESES[Number(f.slice(5, 7)) - 1]}`;
const pct = (x: number): string => `${numero(x, Number.isInteger(x) ? 0 : 1)} %`;
/** Con los decimales del PDF y del renglón (14.26 kWp, no 14.3), sin ceros de sobra en un mensaje («8 kWp», no «8.00 kWp»). */
const kwpTexto = (n: number): string => `${numero(n, n < 100 ? 2 : 1).replace(/\.0+$|(\.\d*?)0+$/, '$1')} kWp`;
const esBuenFin = (p: PromoDelMensaje): boolean => /buen fin/i.test(p.nombre);
/** «Promo Buen Fin» / «Promo de fin de año»: como lo dicen el PDF y el botón. */
const nombrePromo = (p: PromoDelMensaje): string => (esBuenFin(p) ? 'Promo Buen Fin' : `Promo ${p.nombre}`);
/** La mensualidad como la imprime el PDF (pesosMsi de la plantilla): sin centavos si es entera, si no con dos decimales. */
const pesosMsi = (x: number): string => (Math.abs(x - Math.round(x)) < 0.005 ? pesos(x) : `$${numero(x, 2)}`);
/** « o 18 meses sin intereses con tarjeta de crédito» (con su mensualidad si `cifra`), o nada si el PDF de la promo no los lleva. */
const msiDeLaPromo = (p: PromoDelMensaje, cifra: boolean): string =>
  p.msi18 == null ? '' : ` o ${PROMO_MSI_MESES} meses sin intereses${cifra ? ` de ${pesosMsi(p.msi18)}` : ''} con tarjeta de crédito`;

/** Las otras 3 maneras del segundo y del tercero (maneras 1 a 3), sin oferta ni cifras. */
const OTRA_VEZ: Readonly<Record<'segundo' | 'tercero', readonly string[]>> = {
  segundo: [
    '¿Cómo vas con la propuesta? Si la quieres retomar, aquí estoy; si no, también está bien.',
    'Paso a saludarte. Tu propuesta sigue en pie por si la quieres retomar; si ya no, con que me digas está bien.',
    '¿Pudiste darle una vuelta a la propuesta? Si quieres le movemos algo; si no es buen momento, no pasa nada.',
  ],
  tercero: [
    '¿Cómo vas con tu propuesta? Si quieres la dejamos lista, o te busco más adelante.',
    'Te escribo para ver cómo vas. Cuando quieras seguimos con tu propuesta; si prefieres más adelante, me dices.',
    '¿Ya lo pudiste pensar? Si quieres dejamos lista tu propuesta; si no, te busco después.',
  ],
};

/** Las otras 3 maneras del «primero» (maneras 1 a 3): el cliente que regresa con otro folio tras «Murió» ya recibió la de siempre. `de`: «de
 *  tus paneles»…; `mes`: cuándo se le mandó la propuesta. */
const PRIMERO_OTRA_VEZ: readonly ((de: string, mes: string) => string)[] = [
  (de, mes) => `Te escribo por la propuesta ${de} que te mandé en ${mes}. ¿Hay algo que te falte para decidir?`,
  (de, mes) => `¿Pudiste ver la propuesta ${de} que te mandé en ${mes}? Si te quedó alguna duda, aquí estoy.`,
  (de, mes) => `Sigo pendiente de la propuesta ${de} que te mandé en ${mes}. ¿Qué te faltó para decidir?`,
];

/** Cuántas maneras hay de decir cada plantilla: la de siempre y las de OTRA_VEZ / PRIMERO_OTRA_VEZ (hoy 4). Sale de los textos, no se escribe
 *  aparte: con una plantilla de menos textos, la rotación los repetiría. Con 4, ningún texto se repite dentro de los últimos 3 mensajes de un
 *  cliente (maneraDelMensaje). */
export const MANERAS = 1 + Math.min(OTRA_VEZ.segundo.length, OTRA_VEZ.tercero.length, PRIMERO_OTRA_VEZ.length);

export function textoMensaje(plantilla: PlantillaSeguimiento, d: DatosMensaje, hoy: string): string {
  const esCasa = d.tipo === 'casa' || d.tipo === 'baterias' || d.tipo === 'aislado' || d.tipo === 'ampliacion_baterias';
  const nombre = saludoDe(d.cliente_nombre, esCasa);
  const hola = nombre ? `Hola, ${nombre}${/[.!?]$/.test(nombre) ? '' : '.'}` : 'Hola.';
  // Integración promo + «Hoy toca» (2-nov): sin la cifra del motor no se ofrece nada, ni el porcentaje.
  const p = ofertaAplica(d, hoy) ? d.promo : null;
  if (plantilla === 'primero') {
    const f = fechaDe(d.creada_en);
    const mes = `${MESES[Number(f.slice(5, 7)) - 1]}${f.slice(0, 4) === hoy.slice(0, 4) ? '' : ` de ${f.slice(0, 4)}`}`;
    const de = d.tipo === 'ampliacion_baterias' || d.tipo === 'baterias' ? 'de tus baterías' : esCasa ? 'de tus paneles' : 'de los paneles';
    const otra = d.manera ? PRIMERO_OTRA_VEZ[(d.manera - 1) % PRIMERO_OTRA_VEZ.length]! : null;
    return `${hola} Soy ${d.asesor}, de Solarix. ${otra ? otra(de, mes) : `Te mandé la propuesta ${de} en ${mes} y me quedé con la duda: ¿qué te faltó para decidir?`}`;
  }
  // Otra manera de decirlo (maneraDelMensaje): sin repetir la oferta (verificación del 2-nov, ronda 2: dos «tercero» idénticos seguidos; ronda 3:
  // desde la tercera vez salían idénticos; 3-nov: el de hace dos o tres mensajes volvía igual).
  if (d.manera) return `${hola} ${OTRA_VEZ[plantilla][(d.manera - 1) % OTRA_VEZ[plantilla].length]}`;
  // La promo se llama Buen Fin del 3 al 17-nov y su límite es el 17 (promo.ts); después, «Promo de fin de año» al 31-dic. Diego, 3-nov-2026:
  // al cliente se le dice SOLO la promo («20 % de descuento de contado, o 18 meses sin intereses con tarjeta de crédito») y su precio; nunca
  // el 15 % de siempre, «de 15 % a 20 %» ni «en lugar de $…».
  const promoCon = (q: PromoDelMensaje) => `${nombrePromo(q)}: ${pct(q.pct_promo)} de descuento de contado${msiDeLaPromo(q, false)}, hasta el ${diaYMes(q.vence)}.`;
  if (plantilla === 'segundo') {
    if (!p) return `${hola} Te escribo porque ya tienes propuesta con nosotros. ¿La seguimos viendo o la dejamos aquí? Cualquiera está bien.`;
    // Su PDF ya trae la promo: se le recuerda su precio, no se le ofrece «actualizar» a lo que ya tiene.
    if (p.en_su_pdf) return `${hola} Te escribo porque ya tienes propuesta con nosotros, con la ${nombrePromo(p)}: ${pesos(p.contado)} de contado con IVA (${pct(p.pct_promo)} de descuento)${msiDeLaPromo(p, true)}, hasta el ${diaYMes(p.vence)}. ¿La seguimos viendo o la dejamos aquí?`;
    const kwp = p.kwp !== null ? ` de ${kwpTexto(p.kwp)}` : '';
    return `${hola} Te escribo porque ya tienes propuesta con nosotros. Con la ${nombrePromo(p)}, tu sistema${kwp} queda en ${pesos(p.contado)} de contado con IVA (${pct(p.pct_promo)} de descuento)${msiDeLaPromo(p, true)}, hasta el ${diaYMes(p.vence)}. ¿Te la actualizo?`;
  }
  const cierre = '¿La dejamos lista o te busco más adelante?';
  if (!p) return `${hola} Quedamos pendientes con tu propuesta. ${cierre}`;
  // «Tu precio sigue» solo si su PDF ya lo trae: haber recibido el segundo no quiere decir que haya visto una cifra (verificación del 2-nov, ronda 2).
  if (p.en_su_pdf) return `${hola} Tu precio de la ${nombrePromo(p)}, con ${pct(p.pct_promo)} de descuento de contado${msiDeLaPromo(p, false)}, sigue hasta el ${diaYMes(p.vence)}. ${cierre}`;
  return `${hola} ${promoCon(p)} ${cierre}`;
}

/** El mensaje que le toca a este renglón hoy: si ya se le escribió hoy, el MISMO (volver a tocar el botón no le manda el segundo el mismo día).
 *  `h`: la historia del CLIENTE (historiaDe), no solo la de este folio. */
export function plantillaDelRenglon(h: SeguimientoCrm, hoy: string): PlantillaSeguimiento {
  const ultimoMensaje = [...h.toques].reverse().find((t) => t.accion === 'mensaje');
  if (ultimoMensaje?.plantilla && fechaDe(ultimoMensaje.en) === hoy) return ultimoMensaje.plantilla;
  return plantillaDe(h.toques.length ? h : null);
}

/** Qué manera de decir `plantilla` le toca al mensaje de HOY (textoMensaje: 0 es la de siempre, la única que ofrece la promo), según los
 *  mensajes que ya recibió el CLIENTE (`h`: historiaDe todos sus folios, también los cerrados): la que lleva más tiempo sin usarse con esa
 *  plantilla, y entre las nunca usadas, la primera. La cuenta se rehace desde su primer mensaje con esta misma regla, así que a cada mensaje de
 *  antes le toca la manera con que salió. Con MANERAS = 4, una manera no vuelve hasta haber usado las otras 3: un texto nunca es igual a
 *  ninguno de los últimos 3 que recibió, conteste unas veces sí y otras no, o regrese con otro folio tras «Murió» (verificación del 3-nov
 *  sobre e50bf08: «uno sí y uno no» repetía el de hace dos, y el que regresaba recibía el mismo «primero»). El mensaje de hoy ya escrito no
 *  cuenta: reabrir el renglón da el mismo texto. Los textos distintos entre plantillas nunca coinciden.
 *  La manera con que salió cada mensaje se GUARDA en su toque (aplicarMensaje) y se usa tal cual: un folio guarda solo sus últimos MAX_TOQUES
 *  (40) toques, y rehacer la cuenta con esa ventana daba siempre la misma manera pasados 40 (verificación del 3-nov sobre 05e337e: el mismo
 *  texto en cada mensaje desde unos 11 meses de seguimiento). Solo un toque sin `manera` (anotado antes de guardarla) se rehace con la regla. */
export function maneraDelMensaje(h: SeguimientoCrm, plantilla: PlantillaSeguimiento, hoy: string): number {
  const ultimaVez = new Map<string, number>();
  const elegir = (p: PlantillaSeguimiento): number => {
    let mejor = 0;
    for (let m = 1; m < MANERAS; m++) if ((ultimaVez.get(`${p}|${m}`) ?? -1) < (ultimaVez.get(`${p}|${mejor}`) ?? -1)) mejor = m;
    return mejor;
  };
  const mensajes = h.toques.filter((t) => t.accion === 'mensaje' && t.plantilla && fechaDe(t.en) < hoy).sort((a, b) => (a.en < b.en ? -1 : a.en > b.en ? 1 : 0));
  mensajes.forEach((t, i) => ultimaVez.set(`${t.plantilla}|${t.manera ?? elegir(t.plantilla!)}`, i));
  return elegir(plantilla);
}

/** ¿Hace falta la cifra de la promo para el mensaje de hoy? Solo el segundo y el tercero la ofrecen, y solo con su manera de siempre (la 0:
 *  maneraDelMensaje), solo a casas de Hermosillo mientras haya promo: así el servidor no corre el motor por cada renglón, solo por los que la
 *  van a decir. */
export function mensajePideLaPromo(c: Pick<ResumenCotizacion, 'tipo' | 'ciudad'>, h: SeguimientoCrm, hoy: string): boolean {
  const plantilla = plantillaDelRenglon(h, hoy);
  return plantilla !== 'primero' && maneraDelMensaje(h, plantilla, hoy) === 0 && ofertaAplica(c, hoy);
}

/** 52 + 10 dígitos, o null si con ese número WhatsApp no llegaría a nadie: la lista lo dice en vez de abrir un chat equivocado. Acepta
 *  también como se escribían antes los celulares de México: 52 1 662…, 044 662…, 045 662…. */
export function numeroWhatsApp(telefono: string | null | undefined): string | null {
  const d = (telefono ?? '').replace(/\D/g, '');
  const diez = d.length === 12 && d.startsWith('52') ? d.slice(2) : d.length === 13 && /^(521|044|045)/.test(d) ? d.slice(3) : d;
  if (diez.length !== 10 || /^[01]/.test(diez) || /^(\d)\1{9}$/.test(diez)) return null;
  return `52${diez}`;
}

export function enlaceSeguimiento(telefono: string | null | undefined, texto: string): string | null {
  const n = numeroWhatsApp(telefono);
  // Un emoji partido en el nombre haría tronar encodeURIComponent, y con él la lista entera.
  return n ? `https://wa.me/${n}?text=${encodeURIComponent(texto.replace(/[\uD800-\uDFFF]/gu, ''))}` : null;
}

// ───────────── Lo que pasa al tocar ─────────────

const conToque = (seg: SeguimientoCrm, t: ToqueSeguimiento): ToqueSeguimiento[] => [...seg.toques, t].slice(-MAX_TOQUES);

/** Fecha de la próxima vez tras abrir un mensaje: 7 días; con dos sin respuesta, o dos en el mes, se deja en paz. `seg`: la historia del
 *  CLIENTE con este mensaje ya contado. */
export function fechaTrasMensaje(seg: SeguimientoCrm, hoy: string): string {
  if (seg.sin_respuesta >= MAX_MENSAJES_SIN_RESPUESTA) return hoy < PAUSA_HASTA ? PAUSA_HASTA : sumarDias(hoy, 30);
  if (mensajesEnElMes(seg, hoy) >= MAX_MENSAJES_AL_MES) return primeroDelMesSiguiente(hoy);
  return sumarDias(hoy, DIAS_ENTRE_MENSAJES);
}

/** Abrir el mensaje cuenta una vez por día y por cliente: tocar el botón dos veces no gasta el segundo mensaje. `otros`: los OTROS folios del
 *  mismo cliente (historiaDe, con su estado: un cierre reinicia la cuenta): la cuenta y la próxima fecha son del cliente. `vencia`: cuándo le
 *  tocaba al cliente ANTES de este mensaje (venceDelCliente). Un mensaje que se anota aunque no le tocaba («Sí lo mandé» en una pestaña vieja)
 *  nunca adelanta esa fecha: si el cliente pidió que le escribieran el 20, sigue el 20 (verificación del 2-nov, ronda 3: quedaba a los 7 días). */
export function aplicarMensaje(actual: SeguimientoCrm | null | undefined, x: { en: string; por: string; plantilla: PlantillaSeguimiento }, hoy: string, otros: readonly FolioDeHistoria[], vencia: string): { seguimiento: SeguimientoCrm; proximo: string; repetido: boolean } | { repetido: true } {
  const seg = actual ?? SEGUIMIENTO_VACIO;
  const antes = historiaDe([{ seguimiento: seg }, ...otros]);
  if (antes.toques.some((t) => t.accion === 'mensaje' && fechaDe(t.en) === hoy)) return { repetido: true };
  // la manera de decirlo que le tocó HOY (la misma que enseñó el renglón: misma historia del cliente), guardada para no rehacerla después
  const toque: ToqueSeguimiento = { en: x.en, por: x.por, accion: 'mensaje', plantilla: x.plantilla, manera: maneraDelMensaje(antes, x.plantilla, hoy) };
  const seguimiento: SeguimientoCrm = { ...seg, sin_respuesta: seg.sin_respuesta + 1, toques: conToque(seg, toque) };
  const cliente = historiaDe([{ seguimiento: seg }, ...otros, { seguimiento: { ...SEGUIMIENTO_VACIO, toques: [toque] } }]);
  const tras = fechaTrasMensaje(cliente, hoy);
  return { seguimiento, proximo: vencia > tras ? vencia : tras, repetido: false };
}

export interface CambioResultado {
  seguimiento: SeguimientoCrm;
  estado: EstadoCotizacion | null;
  proximo: string | null;
  detalle: string;
  /** Se cierra: los otros folios abiertos del mismo cliente se cierran igual. */
  cierra_cliente: boolean;
  venta?: CuerpoVenta;
}

/** Qué le pasa a la cotización con cada resultado. Truena con el porqué en texto si los datos no sirven. `otros`: los OTROS folios del mismo
 *  cliente: si ya lleva 2 mensajes en el mes, la próxima fecha pasa al mes siguiente aunque haya contestado. */
export function aplicarResultado(actual: SeguimientoCrm | null | undefined, estado: EstadoCotizacion, c: CuerpoResultadoSeguimiento, x: { en: string; por: string }, hoy: string, otros: readonly FolioDeHistoria[]): CambioResultado | { error: string } {
  const seg = actual ?? SEGUIMIENTO_VACIO;
  const toque: ToqueSeguimiento = { en: x.en, por: x.por, accion: c.accion };
  const cliente = historiaDe([{ seguimiento: seg }, ...otros, { seguimiento: { ...SEGUIMIENTO_VACIO, toques: [toque] } }]);
  const nota = 'nota' in c && c.nota ? ` «${c.nota}»` : '';
  // Una visita agendada solo sale en «Hoy toca» después de su fecha: si contestó o sigue interesado, la visita ya pasó y va a Seguimiento.
  // Si se quedara en Visita agendada, su próxima fecha se leería como la de una visita (venceEl le suma un día).
  const aSeguimiento: EstadoCotizacion = 'seguimiento';
  switch (c.accion) {
    case 'contesto': {
      const proximo = primerDiaConCupo(cliente, sumarDias(hoy, DIAS_TRAS_CONTESTAR));
      const tope = proximo > sumarDias(hoy, DIAS_TRAS_CONTESTAR) ? ` Ya lleva ${MAX_MENSAJES_AL_MES} mensajes este mes: se le busca el ${proximo}.` : '';
      return { seguimiento: { ...seg, sin_respuesta: 0, toques: conToque(seg, toque) }, estado: aSeguimiento, proximo, detalle: `Hoy toca: contestó.${nota}${tope}`, cierra_cliente: false };
    }
    case 'interesado': {
      if (c.fecha <= hoy) return { error: 'La próxima fecha tiene que ser después de hoy.' };
      if (c.fecha > sumarDias(hoy, MAX_DIAS_PROXIMA_FECHA)) return { error: 'La próxima fecha no puede pasar de un año.' };
      const proximo = primerDiaConCupo(cliente, c.fecha);
      const tope = proximo !== c.fecha ? ` (ese mes ya lleva ${MAX_MENSAJES_AL_MES} mensajes: queda el ${proximo})` : '';
      return { seguimiento: { ...seg, sin_respuesta: 0, toques: conToque(seg, toque) }, estado: aSeguimiento, proximo, detalle: `Hoy toca: sigue interesado, se le busca el ${c.fecha}${tope}.${nota}`, cierra_cliente: false };
    }
    case 'compro_con_otro': {
      const quien = c.con_quien?.trim() || null;
      const texto = `${quien ? ` con ${quien}` : ''}${c.a_cuanto !== null ? `, a ${pesos(c.a_cuanto)}` : ''}`;
      return { seguimiento: { ...seg, toques: conToque(seg, toque), perdida: { motivo: MOTIVO_COMPRO_CON_OTRO, con_quien: quien, a_cuanto: c.a_cuanto, en: x.en } }, estado: 'perdida', proximo: null, detalle: `Hoy toca: compró${texto || ' con otro'}.`, cierra_cliente: true };
    }
    case 'murio':
      return { seguimiento: { ...seg, toques: conToque(seg, toque), perdida: { motivo: c.motivo, con_quien: null, a_cuanto: null, en: x.en } }, estado: 'perdida', proximo: null, detalle: `Hoy toca: se cerró, ${c.motivo.toLowerCase()}.${nota}`, cierra_cliente: true };
    case 'vendido':
      return { seguimiento: { ...seg, sin_respuesta: 0, toques: conToque(seg, toque) }, estado: 'ganada', proximo: null, detalle: 'Hoy toca: vendido.', cierra_cliente: false, ...(c.venta ? { venta: c.venta } : {}) };
  }
}

// ───────────── Respuestas ─────────────

export interface RenglonHoyToca {
  id: string;
  folio: string;
  revision: number;
  cliente_nombre: string;
  vendedor_id: string;
  vendedor_nombre: string;
  ciudad: string;
  estado: EstadoCotizacion;
  kwp: number | null;
  /** El contado de su propuesta; si la promo del folio ya venció, el normal (promo_vencida). */
  contado: number | null;
  /** La promo del folio ya venció: el contado que se enseña es el normal y el asesor tiene que quitarla antes de otro PDF. */
  promo_vencida: boolean;
  /** Días desde el último contacto desde aquí (mensaje o respuesta), o desde que cambió de estado si nunca se le escribió desde aquí. */
  dias_sin_respuesta: number;
  vence: string;
  mensajes_sin_respuesta: number;
  plantilla: PlantillaSeguimiento;
  texto: string;
  /** Null = no hay a qué número escribirle: se muestra el teléfono y el enlace para corregirlo. */
  whatsapp_url: string | null;
  telefono: string | null;
  /** Borrador con PDF: puede que no se haya mandado. El mensaje dice «te mandé la propuesta»: el asesor debe revisarlo. */
  sin_marcar_enviada: boolean;
  otros_folios: string[];
  /** Folios abiertos con el mismo nombre y OTRO teléfono: si es el mismo cliente, el asesor cierra o cancela el que sobra. */
  parecidos: string[];
  /** Su folio más nuevo está Cancelado (este es uno de antes): si el cliente dijo que no, se cierra con «Murió». */
  cancelado_despues: string | null;
  venta: OpcionesDeVenta;
  /** Las cifras de la promo que dice el mensaje (las del motor), para que el asesor las vea antes de mandarlo. Null = no se ofrece. */
  promo: PromoDelMensaje | null;
}

export interface RespuestaHoyToca {
  hoy: string;
  vendedor_id: string | 'todos';
  tope: number;
  renglones: RenglonHoyToca[];
  escritas_hoy: RenglonHoyToca[];
  atendidas_hoy: number;
  /** Las que tocan y no caben hoy. */
  para_despues: number;
  /** Se llegó al tope de lectura: puede haber clientes que no se ven. */
  lectura_incompleta: boolean;
}

export interface FilaTablero {
  vendedor_id: string;
  nombre: string;
  le_tocaban: number;
  atendidas: number;
  pendientes_total: number;
}

export interface RespuestaTablero {
  hoy: string;
  filas: FilaTablero[];
  motivos: { motivo: string; cuantos: number }[];
  con_otro: { folio: string; cliente: string; vendedor: string; con_quien: string | null; a_cuanto: number | null; en: string }[];
  lectura_incompleta: boolean;
}

/** Tablero de Diego: por vendedor, cuántos le tocaban hoy y cuántos atendió (los que tocó ÉL: atendidasHoy), y por qué se perdieron en los
 *  últimos 30 días. Todo por cliente: un «Murió» que cerró sus 3 folios es un cliente perdido por ese motivo, no 3. */
export function armarTablero(lista: readonly ResumenCotizacion[], usuarios: readonly { id: string; nombre: string; rol: string; activo: boolean }[], hoy: string, incompleta: boolean, ctx: ContextoHoyToca): RespuestaTablero {
  const porVendedor = new Map<string, ResumenCotizacion[]>();
  for (const c of lista) porVendedor.set(c.vendedor_id, [...(porVendedor.get(c.vendedor_id) ?? []), c]);
  const claves = clavesDeClientes(lista, ctx.telefonos);
  const filas: FilaTablero[] = [];
  for (const [id, suyas] of porVendedor) {
    if (ctx.vendedores_sin_hoy_toca.has(id)) continue;
    const { pendientes } = armarHoyToca(suyas, hoy, ctx);
    const atendidas = atendidasHoy(lista, id, hoy, claves);
    if (!pendientes.length && !atendidas) continue;
    filas.push({
      vendedor_id: id,
      nombre: usuarios.find((u) => u.id === id)?.nombre ?? suyas[0]?.vendedor_nombre ?? '(usuario borrado)',
      le_tocaban: Math.min(TOPE_HOY_TOCA, atendidas + pendientes.length),
      atendidas,
      pendientes_total: pendientes.length,
    });
  }
  filas.sort((a, b) => b.pendientes_total - a.pendientes_total || a.nombre.localeCompare(b.nombre));
  const desde = sumarDias(hoy, -30);
  // Los folios que cerró un mismo toque (el mismo cliente, la misma hora) cuentan una vez; los que se cerraron a mano en el CRM, cada uno.
  const porCierre = new Map(lista.map((c) => [c.id, `${claves.get(c.id)}|${c.seguimiento?.perdida?.en ?? c.id}`]));
  const perdidas = unoPorCliente(
    lista.filter((c) => c.estado === 'perdida' && fechaDe(c.estado_desde ?? c.actualizada_en) >= desde),
    porCierre,
  );
  const cuenta = new Map<string, number>();
  for (const c of perdidas) {
    const m = c.seguimiento?.perdida?.motivo ?? 'Sin motivo';
    cuenta.set(m, (cuenta.get(m) ?? 0) + 1);
  }
  const con_otro = perdidas
    .filter((c) => c.seguimiento?.perdida?.motivo === MOTIVO_COMPRO_CON_OTRO)
    .map((c) => ({ folio: c.folio, cliente: c.cliente_nombre, vendedor: c.vendedor_nombre, con_quien: c.seguimiento?.perdida?.con_quien ?? null, a_cuanto: c.seguimiento?.perdida?.a_cuanto ?? null, en: c.seguimiento!.perdida!.en }))
    .sort((a, b) => (a.en < b.en ? 1 : -1));
  return { hoy, filas, motivos: [...cuenta].map(([motivo, cuantos]) => ({ motivo, cuantos })).sort((a, b) => b.cuantos - a.cuantos), con_otro, lectura_incompleta: incompleta };
}

/** Lo que se le pide al renglón además del resumen (el teléfono, las opciones y la promo viven en el borrador o salen del motor). */
export interface ExtraRenglon {
  telefono: string | null;
  venta: OpcionesDeVenta;
  otros_folios: string[];
  asesor: string;
  promo: PromoDelMensaje | null;
  /** La historia del CLIENTE (ArmadoHoyToca.historia). */
  historia: SeguimientoCrm;
  parecidos: string[];
  cancelado_despues: string | null;
  /** contadoDelRenglon del borrador: el contado y si su promo ya venció. */
  contado: { contado: number | null; promo_vencida: boolean };
}

export function renglonHoyToca(c: ResumenCotizacion, extra: ExtraRenglon, hoy: string): RenglonHoyToca {
  const h = extra.historia;
  // Ya se le escribió hoy: se reabre con el MISMO mensaje. Si no, volver a tocar el botón le mandaría el segundo el mismo día.
  const plantilla = plantillaDelRenglon(h, hoy);
  const manera = maneraDelMensaje(h, plantilla, hoy);
  const promo = ofertaAplica(c, hoy) && plantilla !== 'primero' && manera === 0 ? extra.promo : null;
  const texto = textoMensaje(plantilla, { cliente_nombre: c.cliente_nombre, tipo: c.tipo, ciudad: c.ciudad, asesor: asesorDe(extra.asesor), promo, creada_en: c.creada_en, manera }, hoy);
  const ultimo = h.toques[h.toques.length - 1];
  return {
    id: c.id,
    folio: c.folio,
    revision: c.revision,
    cliente_nombre: c.cliente_nombre,
    vendedor_id: c.vendedor_id,
    vendedor_nombre: c.vendedor_nombre,
    ciudad: c.ciudad,
    estado: c.estado,
    kwp: c.kwp_recomendada,
    contado: extra.contado.contado,
    promo_vencida: extra.contado.promo_vencida,
    // Desde el último contacto: si contestó después del mensaje, desde que contestó (verificación adversarial del 2-nov).
    dias_sin_respuesta: Math.max(0, diasEntreFechas(fechaDe(ultimo?.en ?? c.estado_desde ?? c.actualizada_en), hoy)),
    vence: venceDelCliente(c, h, hoy),
    mensajes_sin_respuesta: h.sin_respuesta,
    plantilla,
    texto,
    whatsapp_url: enlaceSeguimiento(extra.telefono, texto),
    telefono: extra.telefono,
    sin_marcar_enviada: c.estado === 'borrador',
    otros_folios: extra.otros_folios,
    parecidos: extra.parecidos,
    cancelado_despues: extra.cancelado_despues,
    venta: extra.venta,
    promo,
  };
}
